import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Button,
  Label,
  Input,
  FormGroup,
  Alert,
  Form,
} from "reactstrap";
import { GetData, PutData, PostFileData } from "../../services/api";
import Breadcrumb from "../../components/Common/Breadcrumb";
import Loader from "../../components/Common/Loader";
import Swal from "sweetalert2";
import "./user-profile.css";

// Lista de zonas horarias comunes
const TIMEZONES = [
  { value: 'America/Mexico_City', label: 'México (Ciudad de México)' },
  { value: 'America/New_York', label: 'Estados Unidos (Nueva York)' },
  { value: 'America/Chicago', label: 'Estados Unidos (Chicago)' },
  { value: 'America/Denver', label: 'Estados Unidos (Denver)' },
  { value: 'America/Los_Angeles', label: 'Estados Unidos (Los Ángeles)' },
  { value: 'America/Bogota', label: 'Colombia (Bogotá)' },
  { value: 'America/Lima', label: 'Perú (Lima)' },
  { value: 'America/Santiago', label: 'Chile (Santiago)' },
  { value: 'America/Buenos_Aires', label: 'Argentina (Buenos Aires)' },
  { value: 'America/Sao_Paulo', label: 'Brasil (São Paulo)' },
  { value: 'Europe/Madrid', label: 'España (Madrid)' },
  { value: 'Europe/London', label: 'Reino Unido (Londres)' },
  { value: 'Europe/Paris', label: 'Francia (París)' },
  { value: 'Asia/Tokyo', label: 'Japón (Tokio)' },
  { value: 'Asia/Shanghai', label: 'China (Shanghai)' },
  { value: 'Australia/Sydney', label: 'Australia (Sídney)' },
];

const UserProfile = () => {
  document.title = "Perfil de Usuario - CRM IHG";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    name: "",
    lastName: "",
    email: "",
    phone: "",
    timezone: "America/Mexico_City",
    companyName: "",
    companyId: "",
    userId: "",
    role: "user",
    profilePicture: null,
  });
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await GetData("/users/profile");
      console.log("Profile response:", response);
      
      // GetData devuelve el objeto completo de axios, los datos están en response.data
      if (response && response.data) {
        const profileData = response.data;
        console.log("Profile data:", profileData);
        
        setProfile({
          name: profileData.name || "",
          lastName: profileData.lastName || "",
          email: profileData.email || "",
          phone: profileData.phone || "",
          timezone: profileData.timezone || "America/Mexico_City",
          companyName: profileData.companyName || "",
          companyId: profileData.companyId || "",
          userId: profileData.id || "",
          role: profileData.role || "user",
          profilePicture: profileData.profilePicture || null,
        });
      } else {
        console.warn("No profile data in response:", response);
        // Si no hay datos, mantener los valores por defecto
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "No se pudo cargar el perfil del usuario",
        confirmButtonColor: "#E9553E",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handlePictureChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      Swal.fire({
        icon: "error",
        title: "Tipo de archivo inválido",
        text: "Solo se permiten imágenes (JPEG, PNG, GIF o WEBP)",
        confirmButtonColor: "#E9553E",
      });
      return;
    }

    // Validar tamaño (máximo 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      Swal.fire({
        icon: "error",
        title: "Archivo muy grande",
        text: "La imagen no puede ser mayor a 5MB",
        confirmButtonColor: "#E9553E",
      });
      return;
    }

    try {
      setUploadingPicture(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await PostFileData("/users/profile/upload-picture", formData);
      
      if (response && response.data && response.data.url) {
        setProfile((prev) => ({
          ...prev,
          profilePicture: response.data.url,
        }));
        
        // Update localStorage
        const authUser = localStorage.getItem("authUser");
        if (authUser) {
          const user = JSON.parse(authUser);
          user.profilePicture = response.data.url;
          localStorage.setItem("authUser", JSON.stringify(user));
        }
        
        // Dispatch custom event to update ProfileMenu
        window.dispatchEvent(new CustomEvent('profilePictureUpdated'));
        
        Swal.fire({
          icon: "success",
          title: "¡Foto actualizada!",
          text: "Tu foto de perfil se ha actualizado correctamente",
          confirmButtonColor: "#E9553E",
        });
      }
    } catch (error) {
      console.error("Error uploading picture:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "No se pudo subir la foto de perfil",
        confirmButtonColor: "#E9553E",
      });
    } finally {
      setUploadingPicture(false);
      // Reset input
      e.target.value = '';
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!profile.name.trim()) {
      newErrors.name = "El nombre es requerido";
    }
    
    if (!profile.lastName.trim()) {
      newErrors.lastName = "El apellido es requerido";
    }
    
    if (!profile.phone.trim()) {
      newErrors.phone = "El teléfono es requerido";
    }
    
    if (!profile.timezone) {
      newErrors.timezone = "La zona horaria es requerida";
    }
    
    // Only validate company name if user is admin
    if (profile.role === "admin" && !profile.companyName.trim()) {
      newErrors.companyName = "El nombre de la compañía es requerido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      Swal.fire({
        icon: "warning",
        title: "Campos incompletos",
        text: "Por favor completa todos los campos requeridos",
        confirmButtonColor: "#E9553E",
      });
      return;
    }

    try {
      setSaving(true);
      const updateData = {
        name: profile.name.trim(),
        lastName: profile.lastName.trim(),
        phone: profile.phone.trim(),
        timezone: profile.timezone,
      };
      
      // Only include companyName if user is admin
      if (profile.role === "admin") {
        updateData.companyName = profile.companyName.trim();
      }
      
      const response = await PutData("/users/profile", updateData);

      if (response && response.data) {
        Swal.fire({
          icon: "success",
          title: "¡Perfil actualizado!",
          text: "Tu perfil se ha actualizado correctamente",
          confirmButtonColor: "#E9553E",
        });
        
        // Update localStorage if needed
        const authUser = localStorage.getItem("authUser");
        if (authUser) {
          const user = JSON.parse(authUser);
          user.username = `${profile.name} ${profile.lastName}`;
          // Keep existing profilePicture if not updated
          if (!user.profilePicture && profile.profilePicture) {
            user.profilePicture = profile.profilePicture;
          }
          localStorage.setItem("authUser", JSON.stringify(user));
        }
        
        // Dispatch custom event to update ProfileMenu
        window.dispatchEvent(new CustomEvent('profileUpdated'));
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "No se pudo actualizar el perfil",
        confirmButtonColor: "#E9553E",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <Loader message="Cargando perfil..." fullScreen={true} size={200} />
      </div>
    );
  }

  return (
    <div className="page-content user-profile">
      <Container fluid>
        <Breadcrumb title="IHubG" breadcrumbItem="Mi Perfil" />

        <Row>
          <Col lg="8" className="mx-auto">
            {/* Información del Usuario */}
            <Card>
              <CardHeader>
                <CardTitle tag="h5" className="mb-0">
                  <i className="ri-user-line me-2"></i>
                  Información del Usuario
                </CardTitle>
              </CardHeader>
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="profile-avatar">
                    <div className="avatar-circle position-relative">
                      {profile.profilePicture ? (
                        <img
                          src={profile.profilePicture}
                          alt="Foto de perfil"
                          className="avatar-image"
                          onError={(e) => {
                            // Si la imagen falla al cargar, mostrar el ícono
                            e.target.style.display = 'none';
                            if (e.target.nextSibling) {
                              e.target.nextSibling.style.display = 'flex';
                            }
                          }}
                        />
                      ) : null}
                      <i 
                        className="ri-user-line"
                        style={{ display: profile.profilePicture ? 'none' : 'flex' }}
                      ></i>
                      <label 
                        htmlFor="profile-picture-input" 
                        className="avatar-upload-overlay"
                        title="Cambiar foto de perfil"
                      >
                        <i className="ri-camera-line"></i>
                      </label>
                      <input
                        type="file"
                        id="profile-picture-input"
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                        onChange={handlePictureChange}
                        style={{ display: 'none' }}
                        disabled={uploadingPicture}
                      />
                      {uploadingPicture && (
                        <div className="avatar-upload-spinner">
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Cargando...</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="profile-info ms-4">
                    <h5 className="mb-2">
                      {profile.name} {profile.lastName}
                    </h5>
                    <p className="text-muted mb-2">
                      <i className="ri-mail-line me-2"></i>
                      {profile.email}
                    </p>
                    <p className="text-muted mb-2">
                      <i className="ri-phone-line me-2"></i>
                      {profile.phone || "Sin teléfono"}
                    </p>
                    <p className="text-muted mb-0">
                      <i className="ri-building-line me-2"></i>
                      {profile.companyName || "Sin compañía"}
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Formulario de Edición */}
            <Card className="mt-4">
              <CardHeader>
                <CardTitle tag="h5" className="mb-0">
                  <i className="ri-settings-3-line me-2"></i>
                  Configuración del Perfil
                </CardTitle>
              </CardHeader>
              <CardBody>
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label className="form-label">
                          Nombre <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="text"
                          name="name"
                          value={profile.name}
                          onChange={handleInputChange}
                          placeholder="Ingresa tu nombre"
                          invalid={!!errors.name}
                        />
                        {errors.name && (
                          <div className="invalid-feedback d-block">
                            {errors.name}
                          </div>
                        )}
                      </FormGroup>
                    </Col>

                    <Col md="6">
                      <FormGroup>
                        <Label className="form-label">
                          Apellido <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="text"
                          name="lastName"
                          value={profile.lastName}
                          onChange={handleInputChange}
                          placeholder="Ingresa tu apellido"
                          invalid={!!errors.lastName}
                        />
                        {errors.lastName && (
                          <div className="invalid-feedback d-block">
                            {errors.lastName}
                          </div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label className="form-label">
                          Email
                        </Label>
                        <Input
                          type="email"
                          name="email"
                          value={profile.email}
                          disabled
                          className="bg-light"
                        />
                        <small className="text-muted">
                          El email no se puede modificar
                        </small>
                      </FormGroup>
                    </Col>

                    <Col md="6">
                      <FormGroup>
                        <Label className="form-label">
                          Teléfono <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="tel"
                          name="phone"
                          value={profile.phone}
                          onChange={handleInputChange}
                          placeholder="Ingresa tu teléfono"
                          invalid={!!errors.phone}
                        />
                        {errors.phone && (
                          <div className="invalid-feedback d-block">
                            {errors.phone}
                          </div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label className="form-label">
                          ID de Compañía
                        </Label>
                        <Input
                          type="text"
                          value={profile.companyId}
                          disabled
                          readOnly
                          className="bg-light"
                          style={{ fontFamily: "monospace" }}
                        />
                        <small className="text-muted">
                          <i className="ri-information-line me-1"></i>
                          Identificador interno (solo lectura)
                        </small>
                      </FormGroup>
                    </Col>

                    <Col md="6">
                      <FormGroup>
                        <Label className="form-label">
                          ID de Usuario
                        </Label>
                        <Input
                          type="text"
                          value={profile.userId}
                          disabled
                          readOnly
                          className="bg-light"
                          style={{ fontFamily: "monospace" }}
                        />
                        <small className="text-muted">
                          <i className="ri-information-line me-1"></i>
                          Identificador interno (solo lectura)
                        </small>
                      </FormGroup>
                    </Col>
                  </Row>

                  {profile.role === "admin" && (
                    <Row>
                      <Col md="12">
                        <FormGroup>
                          <Label className="form-label">
                            Nombre de la Compañía <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="text"
                            name="companyName"
                            value={profile.companyName}
                            onChange={handleInputChange}
                            placeholder="Ingresa el nombre de tu compañía"
                            invalid={!!errors.companyName}
                          />
                          {errors.companyName && (
                            <div className="invalid-feedback d-block">
                              {errors.companyName}
                            </div>
                          )}
                          <small className="text-muted">
                            <i className="ri-information-line me-1"></i>
                            Solo los administradores pueden editar el nombre de la compañía
                          </small>
                        </FormGroup>
                      </Col>
                    </Row>
                  )}

                  <Row>
                    <Col md="12">
                      <FormGroup>
                        <Label className="form-label">
                          Zona Horaria <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="select"
                          name="timezone"
                          value={profile.timezone}
                          onChange={handleInputChange}
                          invalid={!!errors.timezone}
                        >
                          {TIMEZONES.map((tz) => (
                            <option key={tz.value} value={tz.value}>
                              {tz.label}
                            </option>
                          ))}
                        </Input>
                        {errors.timezone && (
                          <div className="invalid-feedback d-block">
                            {errors.timezone}
                          </div>
                        )}
                        <small className="text-muted">
                          <i className="ri-information-line me-1"></i>
                          Esta zona horaria se utilizará para las búsquedas por fecha en el Timeline de Actividades
                        </small>
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="d-flex justify-content-end gap-2 mt-4">
                    <Button
                      type="button"
                      color="secondary"
                      onClick={() => fetchProfile()}
                      disabled={saving}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      color="primary"
                      disabled={saving}
                      style={{ backgroundColor: "#E9553E", borderColor: "#E9553E" }}
                    >
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Guardando...
                        </>
                      ) : (
                        <>
                          <i className="ri-save-line me-2"></i>
                          Guardar Cambios
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default UserProfile;
